<template>
    <div class="home-wrapper">
        <div class="home-page">
            <!-- Background Video -->
            <div class="video-background">
                <video
                    ref="videoElement"
                    autoplay
                    muted
                    loop
                    playsinline
                    preload="metadata"
                    class="background-video"
                >
                    <source
                        src="https://img-wispal.s3.eu-central-1.amazonaws.com/videos/8033008d-1c3b-4999-b937-5ecfc73f625c.mp4"
                        type="video/mp4"
                        media="(min-width: 769px)"
                    />
                    <source
                        src="https://img-wispal.s3.eu-central-1.amazonaws.com/videos/96121aa6-4005-436f-8792-23f1a059fb48.mp4"
                        type="video/mp4"
                        media="(max-width: 768px)"
                    />
                    <source
                        src="https://img-wispal.s3.eu-central-1.amazonaws.com/videos/4c383303-1b4d-4471-b7ea-690ce8a47ec5.webm"
                        type="video/webm"
                        media="(min-width: 769px)"
                    />
                    <source
                        src="https://img-wispal.s3.eu-central-1.amazonaws.com/videos/29465050-53f2-4efa-9c93-b46547bf0355.webm"
                        type="video/webm"
                        media="(max-width: 768px)"
                    />
                </video>
                <div class="video-overlay"></div>
            </div>

            <!-- Header -->
            <header class="home-header">
                <div class="header-left">
                    <img
                        src="@/assets/images/brand/Wispal.svg"
                        alt="Wispal"
                        class="logo"
                    />
                </div>
                <div class="header-right">
                    <LanguageSelector />
                    <!-- Original login button (hidden during closed beta — restore by swapping the two buttons back) -->
                    <button
                        v-if="false"
                        class="login-button"
                        @click="openAuthModal"
                        aria-label="Log In"
                    >
                        {{ $t('general.login') }}
                    </button>
                    <!-- Closed-beta login button: opens the same unavailable modal -->
                    <button
                        class="login-button"
                        @click="openLoginBetaModal"
                        aria-label="Log In"
                    >
                        {{ $t('general.login') }}
                    </button>
                </div>
            </header>

            <!-- Main Content -->
            <main class="home-content">
                <div class="text-content">
                    <h1 class="main-title">
                        {{ $t('home.chat-with') }}
                        {{ $t('home.your-favorite') }}
                        {{ $t('home.creators') }}
                    </h1>
                    <p class="subtitle">
                        {{ $t('home.subtitle-line1') }}
                    </p>
                </div>
                <div class="cta-section">
                    <PrimaryButton
                        :label="$t('home.browse-creators')"
                        @click="browseCreators"
                        classProp="browse-button"
                        buttonType="primary"
                        labelFontSize="18px"
                        labelFontWeight="600"
                    />
                    <PrimaryButton
                        :label="$t('general.go-to-creator')"
                        @click="goToCreator"
                        classProp="creator-cta-button"
                        buttonType="ghost"
                        labelFontSize="18px"
                        labelFontWeight="600"
                    />
                </div>
            </main>

            <!-- Auth Modal -->
            <GenericModal
                v-model="showAuthModal"
                :closable="true"
                :fullscreenMobile="true"
                :darkMode="true"
                contentPadding="0"
            >
                <div class="auth-modal-container">
                    <Continue2 :darkMode="true" />
                </div>
            </GenericModal>

            <!-- Category Modal -->
            <CategorySelectorModal
                v-model="showCategoryModal"
                @category-selected="handleCategorySelect"
            />

            <!-- Login (closed beta) Modal: reuses the same unavailable message -->
            <CategorySelectorModal
                v-model="showLoginBetaModal"
                :directBeta="true"
            />

            <!-- How It Works Modal -->
            <HowItWorks ref="howItWorksRef" />
        </div>
        <AppFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'
import Continue2 from '@/components/authentication/continue2.vue'
import GenericModal from '@/components/shared/core/GenericModal.vue'
import PrimaryButton from '@/components/shared/buttons/primary-button.vue'
import AppFooter from '@/components/shared/AppFooter.vue'
import CategorySelectorModal from '@/components/shared/modals/CategorySelectorModal.vue'
import HowItWorks from '@/components/general-modals/how-it-works.vue'

const router = useRouter()

const videoElement = ref(null)
const showAuthModal = ref(false)
const showCategoryModal = ref(false)
const showLoginBetaModal = ref(false)
const howItWorksRef = ref(null)

const goToCreator = () => {
    router.push('/creator')
}

const browseCreators = () => {
    showCategoryModal.value = true
}

const handleCategorySelect = () => {
    // Closed beta: all categories show the unavailable message in the modal itself
}

const openAuthModal = () => {
    showAuthModal.value = true
}

const openLoginBetaModal = () => {
    showLoginBetaModal.value = true
}

onMounted(() => {
    // Check for invite parameter and redirect if present
    const urlParams = new URLSearchParams(window.location.search)
    const invite = urlParams.get('invite')
    if (invite) {
        router.push({
            path: '/creator',
            query: Object.fromEntries(urlParams.entries()),
        })
    }

    // Ensure video plays on mobile devices
    if (videoElement.value) {
        console.log('Video element found:', videoElement.value)
        videoElement.value.play().catch((error) => {
            console.log('Video autoplay failed:', error)
        })

        // Add event listeners to debug video loading
        videoElement.value.addEventListener('loadeddata', () => {
            console.log('Video loaded successfully')
        })

        videoElement.value.addEventListener('error', (e) => {
            console.error('Video failed to load:', e)
        })
    } else {
        console.error('Video element not found')
    }
})

onBeforeUnmount(() => {
    // Close modals when navigating away
    showAuthModal.value = false
    showCategoryModal.value = false
})
</script>

<style scoped>
.home-wrapper {
    width: 100%;
    min-height: 100dvh;
}

.home-page {
    position: relative;
    width: 100%;
    height: 100dvh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* Video Background */
.video-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    pointer-events: none;
}

.background-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
}

.video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1;
    pointer-events: none;
}

/* Header */
.home-header {
    position: relative;
    z-index: 1001;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
}

.header-left {
    display: flex;
}

.header-left .logo {
    height: 24px;
    width: auto;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.login-button {
    background: #0000007a;
    border: none;
    border-radius: 14px;
    padding: 8px 10px;
    color: white;
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    backdrop-filter: blur(5px);
    transition: all 0.3s ease;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.login-button:hover {
    background: #0000009a;
    transform: translateY(-1px);
}

/* Main Content */
.home-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0 25px 40px;
    position: relative;
    z-index: 1001;
    pointer-events: none;
}

.text-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    max-width: 400px;
    pointer-events: auto;
    margin-top: 120px;
}

.cta-section {
    text-align: center;
    max-width: 400px;
    width: 100%;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.main-title {
    font-size: 36px;
    font-weight: 700;
    color: white;
    margin: 0 0 8px 0;
    line-height: 1.1;
    font-family: 'Basier Circle', sans-serif;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    text-align: center;
    text-wrap: balance;
}

.subtitle {
    font-size: 15px;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
    line-height: 1.4;
    font-family: 'Nunito', sans-serif;
    text-align: center;
    width: 100%;
}

.browse-button {
    width: 100%;
    height: 50px;
    /* border-radius: 30px; */
    margin-bottom: 12px;
    box-shadow: 0 8px 20px rgba(110, 61, 234, 0.3);
}

.browse-button:hover {
    box-shadow: 0 12px 24px rgba(110, 61, 234, 0.4);
}

.creator-cta-button {
    width: 100%;
    height: 50px;
    border-radius: 18px;
    opacity: 0.75;
    font-weight: 500;
}

/* Modal Styles */
.auth-modal-container {
    width: 100%;
    height: 100%;
    border-radius: 14px;
    padding: 40px 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Desktop Styles */
@media (min-width: 769px) {
    .home-header {
        padding: 32px 100px;
    }

    .header-left .logo {
        height: 36px;
    }

    .login-button {
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
        font-family: 'Basier Circle', sans-serif;
        height: 40px;
    }

    .home-content {
        padding: 0 80px 80px;
        flex: unset;
        gap: 90px;
        height: 100%;
        justify-content: center;
    }

    .text-content {
        flex: unset;
        max-width: 600px;
        margin-bottom: 0px;
        margin-top: 120px;
    }

    .cta-section {
        max-width: 600px;
    }

    .main-title {
        font-size: 48px;
        font-weight: 700;
        line-height: 50px;
        margin-bottom: 16px;
    }

    .subtitle {
        font-size: 18px;
        font-weight: 600;
        line-height: 125%;
        color: rgba(255, 255, 255, 0.85);
    }

    .browse-button {
        width: 310px;
        height: 54px;
    }

    .creator-cta-button {
        width: 310px;
        height: 54px;
    }

    .auth-modal-container {
        min-height: 500px;
        padding: 60px 20px;
    }
}
</style>
